import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Image, notification, Tag } from 'antd';
import apiCall from 'apps/investment/apiCalls/userIdentificationImage';
import createIdentificationImageApi from 'apps/investment/apiCalls/userIdentificationImage/mutate';
import ImageEditor from 'common/form/image-editor';
import { CreatePage, ListPage } from 'common/ui/AdminCRUD/page';
import { Button } from 'common/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'common/ui/dialog';
import { Spinner } from 'common/ui/spinner';
import { usePostApi } from 'hooks/api';
import { Pencil, Trash } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  investmentIdentificationImageStatusColors,
  investmentIdentificationImageStatusLabel,
  investmentIdentificationImageTypeColors,
  investmentIdentificationImageTypeLabels,
} from 'utils/constants';
import { formatDateTime } from 'utils/formatDate';
import { cloudflareCdn } from 'utils/image';
import ItemForm from './Form';

// Convert bytes to human readable string (KB/MB/GB)
const formatBytes = bytes => {
  if (bytes === null || bytes === undefined) return '-';
  const b = Number(bytes);
  if (Number.isNaN(b) || b <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

const columnsGenerator = (params, onDelete, isDeleting) => [
  {
    title: 'Code',
    dataIndex: 'code',
  },
  {
    title: 'Type',
    dataIndex: 'image_type',
    render: imageType => {
      return (
        <Tag color={investmentIdentificationImageTypeColors[imageType]}>
          {investmentIdentificationImageTypeLabels[imageType]}
        </Tag>
      );
    },
  },
  {
    title: 'Photo',
    dataIndex: 'image_url',
    render: value =>
      value && (
        <Image
          className="max-h-[333px] aspect-auto"
          src={cloudflareCdn(value)}
        />
      ),
  },
  {
    title: 'Message',
    dataIndex: 'error_message',
  },
  {
    title: 'Size',
    dataIndex: 'file_size',
    render: value => formatBytes(value),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: status => {
      return (
        <Tag color={investmentIdentificationImageStatusColors[status]}>
          {investmentIdentificationImageStatusLabel[status]}
        </Tag>
      );
    },
  },
  {
    title: 'Created',
    dataIndex: 'created_at',
    sorter: true,
    render: value => formatDateTime(value),
  },

  {
    title: 'Action',
    dataIndex: 'id',
    render: (id, record) => {
      return (
        <div className="flex flex-wrap gap-2">
          <EditImageButton {...record} />

          {onDelete ? (
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (confirm('Are you sure?')) {
                  onDelete({
                    userId: record.user_id,
                    identificationImageId: id,
                  });
                }
              }}
            >
              <Trash className="text-red-500" />
            </Button>
          ) : null}
        </div>
      );
    },
  },
];

const EditImageButton = ({ image_url, user_id, image_type }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);
  const queryClient = useQueryClient();

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: createIdentificationImageApi,
    onSuccess: () => {
      notification.open({
        type: 'success',
        message: 'Success',
      });
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: [apiCall.list.queryKey] });
    },
    onError: error => {
      notification.open({
        type: 'error',
        message: `Error uploading image:: ${JSON.stringify(error)}`,
      });
    },
  });

  const handleSaveImage = (imageFile, previewUrl, metadata) => {
    try {
      const formData = new FormData();

      formData.append('file', imageFile);
      formData.append('image_type', image_type);

      mutate({
        userId: user_id,
        formData,
      });
    } catch (error) {
      console.error('Error preparing image upload:', error);
    }
  };

  return (
    <>
      <Button variant="outline" size="icon" onClick={() => setIsOpen(true)}>
        <Pencil className="text-blue-500" />
      </Button>
      <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{`Edit ${image_type}`}</DialogTitle>
          </DialogHeader>
          {isOpen ? (
            <ImageEditor
              imageUrl={cloudflareCdn(image_url)}
              onClose={onClose}
              onSave={handleSaveImage}
            />
          ) : (
            <div />
          )}
          {isLoading && (
            <div className="absolute bg-opacity-70 bg-white w-full h-full z-30 flex justify-center items-center">
              <Spinner size="lg" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const InvestmentUserIdentificationImageListPage = (
  props,
  { params }
) => {
  const routeParams = useParams();
  const queryClient = useQueryClient();

  const {
    post: onDelete,
    isLoading: isDeleting,
    error: deleteItemError,
  } = usePostApi(apiCall.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiCall.list.queryKey] });
    },
  });

  return (
    <ListPage
      title="All Identification Images"
      apiCall={apiCall}
      params={params}
      createUrl={`/investment/users/${routeParams.userId}/identification-images/create`}
      columnGenerator={props => columnsGenerator(props, onDelete, isDeleting)}
      resourceName="data"
      {...props}
    />
  );
};

export const InvestmentUserIdentificationImageCreatePage = props => {
  const routeParams = useParams();

  return (
    <CreatePage
      title="Identification Images"
      apiCall={apiCall}
      listUrl={`/investment/users/${routeParams.userId}/identification-images/create`}
      ItemForm={ItemForm}
      resourceName="data"
      {...props}
    />
  );
};
